/**
 * Face matching, entirely on the device.
 *
 * The Worker stores *geometry* — where faces are in a photo — and never a
 * biometric. This module is the other half: crop each face region, turn it
 * into a compact "print" (a 64-number descriptor), and compare prints with the
 * guest's selfie. Nothing here leaves the browser, which is why the feature
 * can exist on a free plan without a face-recognition service and its terms.
 *
 * Prints are deliberately crude: a 12×12 luminance fingerprint is a match for
 * "same face, same party, same lighting" and nothing better. It tolerates the
 * exact situations a wedding creates (same room, same default camera) and will
 * confidently mis-match identical-looking strangers less than a real embedding
 * would, because all it can say is "the face's shading pattern is similar".
 * The UI frames results as suggestions, never facts.
 */

interface CropSource {
  /** The analysed geometry, fractions 0..1 against the thumbnail. */
  readonly xMin: number;
  readonly yMin: number;
  readonly xMax: number;
  readonly yMax: number;
}

/** Print dimension (72 luminance values). */
const PRINT_SIZE = 12;

export type FacePrint = Float32Array;

/**
 * Crop one face box out of an image element and produce its print.
 *
 * Crops from `img` (already loaded) rather than a URL so the same loaded
 * element can serve every face box without re-decoding. The box is centred and
 * expanded 30% so an upward-tilted face is not cropped to a chin.
 */
export async function printForBox(
  img: HTMLImageElement,
  box: CropSource,
): Promise<FacePrint | null> {
  const width = img.naturalWidth;
  const height = img.naturalHeight;
  if (!width || !height) return null;

  // Centre + expansion in image pixels.
  const cx = ((box.xMin + box.xMax) / 2) * width;
  const cy = ((box.yMin + box.yMax) / 2) * height;
  const side = Math.max((box.xMax - box.xMin) * width, (box.yMax - box.yMin) * height) * 1.3;

  const sx = Math.max(0, cx - side / 2);
  const sy = Math.max(0, cy - side / 2);
  const sw = Math.min(width - sx, side);
  const sh = Math.min(height - sy, side);
  if (sw < 4 || sh < 4) return null;

  const canvas = document.createElement("canvas");
  canvas.width = PRINT_SIZE;
  canvas.height = PRINT_SIZE;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;

  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, PRINT_SIZE, PRINT_SIZE);
  return printFromCanvas(canvas);
}

/** The print for an arbitrary image (a guest's selfie), full frame. */
export async function printForImage(source: Blob): Promise<FacePrint | null> {
  try {
    const bitmap = await createImageBitmap(source);
    const canvas = document.createElement("canvas");
    canvas.width = PRINT_SIZE;
    canvas.height = PRINT_SIZE;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0, PRINT_SIZE, PRINT_SIZE);
    bitmap.close();
    return printFromCanvas(canvas);
  } catch {
    return null;
  }
}

function printFromCanvas(canvas: HTMLCanvasElement): FacePrint | null {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return null;

  const frame = ctx.getImageData(0, 0, PRINT_SIZE, PRINT_SIZE);
  const print = new Float32Array(PRINT_SIZE * PRINT_SIZE);

  for (let i = 0; i < print.length; i++) {
    const r = frame.data[i * 4] ?? 0;
    const g = frame.data[i * 4 + 1] ?? 0;
    const b = frame.data[i * 4 + 2] ?? 0;
    // Luma, normalised to unit scale so lighting differences move the *mean*,
    // which distance() removes, not the *shape*.
    print[i] = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  }

  return print;
}

/** Distance in 0..1; below ~0.22 is the same face at a party's light. */
export function distance(a: FacePrint, b: FacePrint): number {
  if (a.length !== b.length) return 1;

  // Mean removal first: a selfie shot in daylight and a dance-floor crop
  // differ mostly in brightness, which is the least interesting dimension.
  let meanA = 0;
  let meanB = 0;
  for (let i = 0; i < a.length; i++) {
    meanA += a[i]!;
    meanB += b[i]!;
  }
  meanA /= a.length;
  meanB /= b.length;

  let total = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i]! - meanA - (b[i]! - meanB);
    total += Math.abs(d);
  }
  return total / a.length;
}

/** The threshold the UI shows a "you" badge above. Tuned on party crops; the *result* set is still labelled "may be you". */
export const FACE_MATCH_THRESHOLD = 0.22;

export interface FaceHit {
  readonly mediaId: string;
  readonly displayUrl: string;
  readonly thumbUrl: string;
  readonly score: number;
}

/** Smallest distance first — the best guess on top. */
export function bestMatches(
  selfiePrint: FacePrint,
  candidates: FaceHit[],
  prints: Map<string, FacePrint[]>,
  limit = 12,
): FaceHit[] {
  const scored: Array<FaceHit> = [];

  for (const hit of candidates) {
    const facePrints = prints.get(hit.mediaId);
    if (!facePrints) continue;
    let best = 1;
    for (const print of facePrints) {
      const d = distance(selfiePrint, print);
      if (d < best) best = d;
    }
    if (best <= FACE_MATCH_THRESHOLD) {
      scored.push({ ...hit, score: best });
    }
  }

  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limit);
}
